import axios from 'axios';
import getQueryParams from 'utils/getQueryParams';
import config from 'constants/apiConfig';
import posthog from 'posthog-js';
import {
  CREATE_OR_GET_USER_REQUEST,
  UPDATE_INCIDENT_STATUS,
  CREATE_OR_GET_USER_SUCCESS,
  CREATE_OR_GET_USER_ERROR,
  FETCH_GROUPS_REQUEST,
  FETCH_GROUPS_SUCCESS,
  FETCH_GROUPS_ERROR,
  FETCH_GROUPS_FOR_USER_REQUEST,
  FETCH_GROUPS_FOR_USER_SUCCESS,
  FETCH_GROUPS_FOR_USER_ERROR,
  CREATE_GROUP_REQUEST,
  CREATE_GROUP_SUCCESS,
  CREATE_GROUP_ERROR,
  FETCH_INCIDENTS_REQUEST,
  FETCH_INCIDENTS_SUCCESS,
  FETCH_INCIDENTS_ERROR,
  INVITE_GROUP_MEMBER_REQUEST,
  INVITE_GROUP_MEMBER_SUCCESS,
  INVITE_GROUP_MEMBER_ERROR,
  UPDATE_CURRENTLY_SELECTED_REQUEST,
  UPDATE_CURRENTLY_SELECTED_SUCCESS,
  UPDATE_CURRENTLY_SELECTED_ERROR,
  UPDATE_USER_PROFILE_SETTINGS_REQUEST,
  UPDATE_USER_PROFILE_SETTINGS_SUCCESS,
  UPDATE_USER_PROFILE_SETTINGS_ERROR,
  BULK_USER_IMPORT_REQUEST,
  BULK_USER_IMPORT_SUCCESS,
  BULK_USER_IMPORT_ERROR,
  GET_USER_BY_ID_REQUEST,
  GET_USER_BY_ID_SUCCESS,
  GET_USER_BY_ID_ERROR,
  FETCH_USER_GROUPS_ROLES_INCIDENTS_REQUEST,
  FETCH_USER_GROUPS_ROLES_INCIDENTS_SUCCESS,
  FETCH_USER_GROUPS_ROLES_INCIDENTS_ERROR,
} from 'constants/profile-action-types';

import {
  fetchRostersForGroups,
  fetchAllOrgScopedRolesInGroup,
} from './roleActions';

import { getRosterForIncident } from 'actions/incidentActions';

import { toast } from 'react-toastify';
import { endLoading } from 'reducers/loading/loading.action';
import {
  updateUserIdAndName,
  updateUserRole,
  updateUserTimeFormat,
} from 'PREPAREsrc/store/user/user.action';
import { changeSelectedWorkSpace } from 'PREPAREsrc/store/workspaces/workspaces.action';
import { noAutoClose } from 'assets/data/config';
import { FETCH_ALL_USER_WORKSPACE } from 'constants/workspace-action-types';

function createOrGetUserRequest() {
  return {
    type: CREATE_OR_GET_USER_REQUEST,
  };
}

export const createOrGetUserSuccess = (user) => ({
  type: CREATE_OR_GET_USER_SUCCESS,
  payload: user,
});

function createOrGetUserError(error) {
  const errorMessage =
    'createOrGetUserError\n' +
    ((!!error &&
      !!error.message &&
      typeof error.message === 'string' &&
      error.message) ||
      '') +
    '\nIf you continue to experience this error, contact support@disastertech.com';

  return {
    type: CREATE_OR_GET_USER_ERROR,
    payload: errorMessage,
  };
}

export function createOrGetUser({ userName }) {
  return (dispatch) => {
    dispatch(createOrGetUserRequest());
    return axios(config.apiGateway.createOrGetUser, {
      method: 'POST',
      data: JSON.stringify({
        email_address: userName,
      }),
    })
      .then((response) => {
        const user = response.data || {};
        dispatch(createOrGetUserSuccess(user));
        dispatch(updateUserIdAndName(user.epic_user_name, user.epic_user_id));
        dispatch(changeSelectedWorkSpace(user.epic_user_lastWorkspaceId));
        dispatch(updateUserTimeFormat(user.timeFormat));
        dispatch(updateUserRole(user.roles));

        // Identify user in PostHog (queues if not yet initialized)
        if (user.user_guid) {
          try {
            posthog.identify(user.user_guid, {
              email: userName,
              name: user.name || userName,
            });
          } catch (error) {
            console.warn('Failed to identify user in PostHog:', error);
          }
        }

        return user;
      })
      .catch((error) => {
        if (
          error.message ===
          "Missing Refresh Token (audience: 'https://dev-3w6tz0rvxhgsxz4h.us.auth0.com/api/v2/', scope: 'openid profile email offline_access')"
        ) {
          localStorage.clear();
          sessionStorage.clear();
          window.location.reload();
          return;
        }
        dispatch(createOrGetUserError(error));
      });
  };
}

function getUserByIdRequest() {
  return {
    type: GET_USER_BY_ID_REQUEST,
  };
}

export const getUserByIdSuccess = (user) => ({
  type: GET_USER_BY_ID_SUCCESS,
  payload: user,
});

function getUserByIdError() {
  const errorMessage =
    'You do not have permission to view this user’s profile.';
  console.warn(errorMessage);
  toast.error(errorMessage);
  return {
    type: GET_USER_BY_ID_ERROR,
  };
}

export function getUserById(userGuid) {
  return (dispatch) => {
    dispatch(getUserByIdRequest());
    return axios(config.apiGateway.getUserById, {
      method: 'POST',
      data: JSON.stringify({
        focus_user_id: userGuid,
      }),
    })
      .then((response) => {
        const user = response.data || {};
        dispatch(getUserByIdSuccess(user));

        return user;
      })
      .catch(() => {
        dispatch(getUserByIdError());
      })
      .finally(() => dispatch(endLoading()));
  };
}

function fetchGroupsForUserRequest() {
  return {
    type: FETCH_GROUPS_FOR_USER_REQUEST,
  };
}

export const fetchGroupsForUserSuccess = (groups, userGuid) => ({
  type: FETCH_GROUPS_FOR_USER_SUCCESS,
  payload: {
    userGuid,
    groups,
  },
});

function fetchGroupsForUserError(error) {
  const detail =
    error?.response?.data?.publicMessage ||
    error?.response?.data?.message ||
    (typeof error?.message === 'string' && error.message) ||
    `HTTP ${error?.response?.status ?? 'unknown'}`;
  const errorMessage =
    'fetchGroupsError\n\n' +
    detail +
    '\n\nIf you continue to experience this error, contact support@disastertech.com';
  console.error(errorMessage, error);
  toast.error(errorMessage, noAutoClose);
  return {
    type: FETCH_GROUPS_FOR_USER_ERROR,
  };
}

export const fetchGroupsForUser = ({ user_guid }) => {
  return (dispatch) => {
    dispatch(fetchGroupsForUserRequest());
    return axios(config.apiGateway.fetchGroupsForUser, {
      method: 'POST',
      data: JSON.stringify({
        user_guid,
      }),
    })
      .then((response) => {
        const groups = response.data || {};
        dispatch(fetchGroupsForUserSuccess(groups, user_guid));

        return groups;
      })
      .catch((error) => {
        dispatch(fetchGroupsForUserError(error));
      });
  };
};

function fetchGroupsRequest() {
  return {
    type: FETCH_GROUPS_REQUEST,
  };
}

export const fetchGroupsSuccess = (groups, group_id) => ({
  type: FETCH_GROUPS_SUCCESS,
  payload: { groups, group_id },
});

function fetchGroupsError(error) {
  const detail =
    error?.response?.data?.publicMessage ||
    error?.response?.data?.message ||
    (typeof error?.message === 'string' && error.message) ||
    `HTTP ${error?.response?.status ?? 'unknown'}`;
  const errorMessage =
    'fetchGroupsError\n\n' +
    detail +
    '\n\nIf you continue to experience this error, contact support@disastertech.com';
  console.error(errorMessage, error);
  toast.error(errorMessage, noAutoClose);
  return {
    type: FETCH_GROUPS_ERROR,
  };
}

export const fetchGroups = () => {
  const queryParams = getQueryParams(document.location.search);
  const pathname = document.location.pathname;

  let pathIncidentId;
  if (pathname.includes('iap')) {
    const splitPathname = pathname.split('/');
    pathIncidentId = splitPathname[2];
  }
  return (dispatch, getState) => {
    const { user_guid } = getState().app.user;

    dispatch(fetchGroupsRequest());
    return axios(config.apiGateway.fetchGroupsForUser, {
      method: 'POST',
      data: JSON.stringify({
        user_guid,
      }),
    })
      .then(async (response) => {
        const groups = response.data || {};
        if (!!queryParams.incident || pathIncidentId) {
          const queryIncident = queryParams.incident || pathIncidentId;
          await axios(config.apiGateway.getGroupFromIncidentId, {
            method: 'POST',
            data: {
              incident: queryIncident,
            },
          })
            .then((groupResponse) => {
              if (groupResponse.data.length == 0) {
                toast.error('Something went wrong in URL.');
                dispatch(fetchGroupsSuccess(groups));
                dispatch(fetchIncidents());
                return;
              }
              const group_Id = groupResponse.data[0].group_id || null;

              if (!group_Id) {
                toast.error('Something went wrong');
                dispatch(fetchGroupsSuccess(groups));
                dispatch(fetchIncidents());
                return;
              }

              const isValidGroupForUser = response.data.some((group) => {
                return group.group_guid === group_Id;
              });

              if (!isValidGroupForUser) {
                toast.error('User does not have permission for this incident.');
                dispatch(fetchGroupsSuccess(groups));
                dispatch(fetchIncidents());
                return;
              }

              dispatch(fetchGroupsSuccess(groups, group_Id));
              dispatch(fetchIncidents(queryIncident));
            })
            .catch((error) => {
              console.log('Fetch Group by incident error', error);
              toast.error('Something went wrong.');
              dispatch(fetchGroupsSuccess(groups));
              dispatch(fetchIncidents());
            });
        } else {
          dispatch(fetchGroupsSuccess(groups));
          dispatch(fetchIncidents());
        }
        return groups;
      })
      .catch((error) => {
        dispatch(fetchGroupsError(error));
      });
  };
};

function createGroupRequest() {
  return {
    type: CREATE_GROUP_REQUEST,
  };
}

export const createGroupSuccess = ({ group_name, groups }) => ({
  groups,
  group_name,
  type: CREATE_GROUP_SUCCESS,
});

function createGroupError(error) {
  const errorMessage =
    'createGroupError\n\n' +
    ((!!error &&
      !!error.message &&
      typeof error.message === 'string' &&
      error.message) ||
      '') +
    '\n\nIf you continue to experience this error, contact support@disastertech.com';
  console.error(errorMessage);
  toast.error(errorMessage, noAutoClose);
  return {
    type: CREATE_GROUP_ERROR,
  };
}

export const createGroup = ({ groupName }) => {
  return (dispatch, getState) => {
    dispatch(createGroupRequest());

    const {
      user: { user_guid },
      groups: existingGroups,
    } = getState().app;
    return axios(config.apiGateway.createGroup, {
      method: 'POST',
      data: JSON.stringify({
        user_guid,
        group_name: groupName.trim(),
      }),
    })
      .then((response) => {
        const groups = response.data || existingGroups;
        const newGroup = groups.find((g) => g.group_name === groupName);
        if (newGroup) {
          dispatch(fetchIncidents());
          dispatch(fetchRostersForGroups([newGroup.group_guid]));
          dispatch(fetchAllOrgScopedRolesInGroup(newGroup.group_guid));
          dispatch(fetchUserGroupsRolesIncidents());
        }
        dispatch(
          createGroupSuccess({
            group_name: groupName.trim(),
            groups,
          })
        );
      })
      .catch((error) => {
        if (
          error &&
          error.response &&
          error.response.data &&
          JSON.stringify(error.response.data) ===
            '{"ERROR":"Group Name Exists"}'
        ) {
          dispatch(
            createGroupError({
              message: 'This Group name is in use.  Please enter a new one.',
            })
          );
        } else {
          dispatch(createGroupError(error));
        }
      });
  };
};

function fetchIncidentsRequest() {
  return {
    type: FETCH_INCIDENTS_REQUEST,
  };
}

const fetchIncidentsSuccess = (incidents, queryIncidentId) => ({
  type: FETCH_INCIDENTS_SUCCESS,
  payload: { incidents, queryIncidentId },
});

function fetchIncidentsError(error) {
  const errorMessage =
    'fetchIncidentsError\n\n' +
    ((!!error &&
      !!error.message &&
      typeof error.message === 'string' &&
      error.message) ||
      '') +
    '\n\nIf you continue to experience this error, contact support@disastertech.com';
  console.error(errorMessage);
  toast.error(errorMessage, noAutoClose);
  return {
    type: FETCH_INCIDENTS_ERROR,
  };
}

function updateIncidentStatus(status) {
  return {
    type: UPDATE_INCIDENT_STATUS,
    payload: status,
  };
}

export const fetchCurrentIncidentStatus = () => async (dispatch, getState) => {
  const incidentId = getState().app.currentIncident?.id;
  if (!incidentId) return;
  try {
    const response = await axios(config.apiGateway.fetchIncidentStatus, {
      method: 'POST',
      data: JSON.stringify({
        incidentId,
      }),
    });
    const incident = response.data || {};
    const status =
      incident && Array.isArray(incident) && incident[0]?.teams_status;

    if (status) {
      dispatch(updateIncidentStatus(status));
    }
  } catch (error) {
    dispatch(fetchIncidentsError(error));
  }
};

export const fetchIncidents = (queryIncidentId = null) => {
  return (dispatch, getState) => {
    dispatch(fetchIncidentsRequest());
    const { user_guid } = getState().app.user;
    return axios(config.apiGateway.fetchIncidents, {
      method: 'POST',
      data: JSON.stringify({
        user_guid,
      }),
    })
      .then((response) => {
        const incidents = response.data || {};

        dispatch(fetchIncidentsSuccess(incidents, queryIncidentId));

        return incidents;
      })
      .catch((error) => {
        dispatch(fetchIncidentsError(error));
      })
      .finally(() => {
        dispatch(endLoading());
      });
  };
};

function inviteGroupMemberRequest() {
  return {
    type: INVITE_GROUP_MEMBER_REQUEST,
  };
}

function inviteGroupMemberSuccess() {
  return {
    type: INVITE_GROUP_MEMBER_SUCCESS,
  };
}

export function inviteGroupMemberError(error) {
  const errorMessage =
    'inviteGroupMemberError\n\n' +
    ((!!error &&
      !!error.message &&
      typeof error.message === 'string' &&
      error.message) ||
      '') +
    '\n\nIf you continue to experience this error, contact support@disastertech.com';
  // TODO CRITICAL causing bugs so suppressing warnings but that may not be what we want
  //console.error(errorMessage)
  //toast.error(errorMessage)
  return {
    type: INVITE_GROUP_MEMBER_ERROR,
    payload: errorMessage,
  };
}

export const inviteGroupMember = ({
  groupGuid: group_guid,
  email: email_address,
  name: name,
}) => {
  return (dispatch) => {
    dispatch(inviteGroupMemberRequest());
    return axios(config.apiGateway.inviteGroupMember, {
      method: 'POST',
      data: JSON.stringify({
        group_guid,
        email_address,
        name,
      }),
    })
      .then((response) => {
        const data = response.data;
        const error = data['ERROR'];

        if (error) {
          dispatch(inviteGroupMemberError(error));
        } else {
          dispatch(inviteGroupMemberSuccess());
          dispatch(fetchRostersForGroups([group_guid]));
        }
        // toast.success('Invite Group Member successful', {
        //   autoClose: 2000,
        // });
      })
      .catch((error) => {
        if (error.message.includes('401')) {
          // there will be more, but this is the one I have right now
          dispatch(
            inviteGroupMemberError({
              message: 'You do not have the correct permissions.',
            })
          );
        } else {
          dispatch(
            inviteGroupMemberError({ message: 'User already in Group.' })
          );
        }
      });
  };
};

function updateCurrentlySelectedRequest() {
  return {
    type: UPDATE_CURRENTLY_SELECTED_REQUEST,
  };
}

function updateCurrentlySelectedSuccess(currently_selected) {
  return {
    type: UPDATE_CURRENTLY_SELECTED_SUCCESS,
    payload: currently_selected,
  };
}

export function updateCurrentlySelectedError(error) {
  const errorMessage =
    'updateCurrentlySelectedError\n\n' +
    ((!!error &&
      !!error.message &&
      typeof error.message === 'string' &&
      error.message) ||
      '') +
    '\n\nIf you continue to experience this error, contact support@disastertech.com';
  console.error(errorMessage);
  toast.error(errorMessage, noAutoClose);
  return {
    type: UPDATE_CURRENTLY_SELECTED_ERROR,
  };
}

export const updateCurrentlySelected = ({
  groupId = null,
  route = null,
  aor_id = null,
  incidentId = '',
}) => {
  return (dispatch, getState) => {
    dispatch(updateCurrentlySelectedRequest());
    const previousSelected = getState().app.user.currently_selected;
    const newSelected = {
      group_guid: groupId ?? previousSelected.group_guid,
      route: route ?? previousSelected.route,
      aor_id: aor_id ?? previousSelected.aor_id,
      incident_id:
        incidentId !== '' ? incidentId : previousSelected.incident_id,
    };
    return axios(config.apiGateway.updateCurrentlySelected, {
      method: 'POST',
      data: JSON.stringify({
        currently_selected: newSelected,
      }),
    })
      .then(() => {
        if (groupId) dispatch(fetchAllOrgScopedRolesInGroup(groupId));
        dispatch(updateCurrentlySelectedSuccess(newSelected));
      })
      .catch((error) => {
        dispatch(updateCurrentlySelectedError(error));
      });
  };
};

function updateUserProfileSettingsRequest() {
  return {
    type: UPDATE_USER_PROFILE_SETTINGS_REQUEST,
  };
}

function updateUserProfileSettingsSuccess({ newSettings }) {
  return {
    payload: newSettings,
    type: UPDATE_USER_PROFILE_SETTINGS_SUCCESS,
  };
}

export function updateUserProfileSettingsError(error) {
  const errorMessage =
    'updateUserProfileSettingsError\n\n' +
    ((!!error &&
      !!error.message &&
      typeof error.message === 'string' &&
      error.message) ||
      '') +
    '\n\nIf you continue to experience this error, contact support@disastertech.com';
  console.error(errorMessage);
  toast.error(errorMessage, noAutoClose);
  return {
    type: UPDATE_USER_PROFILE_SETTINGS_ERROR,
  };
}

export const updateUserProfileSettings = (userId, newSettings) => {
  return (dispatch, getState) => {
    dispatch(updateUserProfileSettingsRequest());
    return axios(config.apiGateway.updateUserProfileSettings, {
      method: 'POST',
      data: JSON.stringify({
        user_id: userId,
        newSettings,
      }),
    })
      .then(() => {
        if (userId === getState().app.user.user_guid)
          dispatch(updateUserProfileSettingsSuccess({ newSettings }));
        const { currentlySelectedGroup } = getState().app;
        const { currentIncident } = getState().app;
        if (currentlySelectedGroup)
          dispatch(fetchRostersForGroups([currentlySelectedGroup.group_guid]));
        if (currentIncident) dispatch(getRosterForIncident());
        dispatch(getUserById(userId));
      })
      .catch((error) => {
        dispatch(updateUserProfileSettingsError(error));
      });
  };
};

function bulkUserImportRequest() {
  return {
    type: BULK_USER_IMPORT_REQUEST,
  };
}

function bulkUserImportSuccess(bulkImportedUsers) {
  return {
    type: BULK_USER_IMPORT_SUCCESS,
    payload: bulkImportedUsers,
  };
}

export const bulkUserImport = (template, group) => {
  return (dispatch) => {
    dispatch(bulkUserImportRequest());
    var formData = new FormData();
    formData.append('template', template);
    formData.append('group', JSON.stringify(group));

    return axios
      .post(config.apiGateway.bulkUserImport, formData, {
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        const bulkImportedUsers = response.data || {};
        dispatch(bulkUserImportSuccess(bulkImportedUsers));
        dispatch(fetchRostersForGroups([group.group_guid]));
        return bulkImportedUsers;
      })
      .catch((error) => {
        if (
          error.response &&
          error.response.data &&
          error.response.data.ERROR &&
          error.response.data.ERROR.includes('Invalid email address')
        ) {
          toast.error(
            '' +
              error.response.data.ERROR +
              '\n\nPlease ensure all email addresses in your import file are valid and try again.',
            noAutoClose
          );
        } else {
          //dispatch(bulkUserImportError({...error,message: error.response.data.ERROR}));
          dispatch(endLoading());
          toast.error(
            'bulkUserImportError\n\n' +
              ((!!error &&
                !!error.message &&
                typeof error.message === 'string' &&
                error.message) ||
                '') +
              '\n\nIf you continue to experience this error, contact support@disastertech.com',
            noAutoClose
          );
        }
        dispatch({ type: BULK_USER_IMPORT_ERROR });
      });
  };
};

export const toggleAllowAdminProfileEdit = (id) => {
  return (dispatch) => {
    return axios(config.apiGateway.toggleAllowAdminProfileEdit, {
      method: 'POST',
      data: JSON.stringify({
        group_guid: id,
      }),
    })
      .then(() => {
        dispatch(fetchGroups());
        dispatch(fetchUserGroupsRolesIncidents());
        // toast.success('Toggle Allow Admin Profile Edit successful', {
        //   autoClose: 2000,
        // });
      })
      .catch((error) => {
        dispatch(updateUserProfileSettingsError(error));
      });
  };
};

function fetchUserGroupsRolesIncidentsRequest() {
  return {
    type: FETCH_USER_GROUPS_ROLES_INCIDENTS_REQUEST,
  };
}

function fetchUserGroupsRolesIncidentsSuccess(userGroupsRolesIncidents) {
  return {
    type: FETCH_USER_GROUPS_ROLES_INCIDENTS_SUCCESS,
    payload: userGroupsRolesIncidents,
  };
}

export function profileActionsError(error) {
  const errorMessage =
    'profileActions Error\n\n' +
    ((!!error &&
      !!error.message &&
      typeof error.message === 'string' &&
      error.message) ||
      '') +
    '\n\nIf you continue to experience this error, contact support@disastertech.com';
  console.error(errorMessage);
  toast.error(errorMessage, noAutoClose);
  return {
    type: FETCH_USER_GROUPS_ROLES_INCIDENTS_ERROR,
  };
}

export const fetchUserGroupsRolesIncidents = (user_guid) => {
  return (dispatch) => {
    dispatch(fetchUserGroupsRolesIncidentsRequest());
    return axios(config.apiGateway.fetchUserGroupsRolesIncidents, {
      method: 'POST',
      data: JSON.stringify({
        user_guid: user_guid,
      }),
    })
      .then((response) => {
        const userGroupsRolesIncidents = response.data || [];
        dispatch(
          fetchUserGroupsRolesIncidentsSuccess(userGroupsRolesIncidents)
        );
      })
      .catch((error) => {
        dispatch(profileActionsError(error));
      });
  };
};

export const getAllUserWorkspace = (user_guid) => {
  return (dispatch, getState) => {
    if (!getState().app?.currentlySelectedGroup) {
      return;
    }
    return axios(config.apiGateway.getAllUserWorkspace, {
      method: 'POST',
      data: JSON.stringify({
        user_guid,
        group_guid: getState().app?.currentlySelectedGroup?.group_guid,
      }),
    })
      .then((response) => {
        dispatch({ type: FETCH_ALL_USER_WORKSPACE, payload: response.data });
      })
      .catch((error) => {
        dispatch(profileActionsError(error));
      });
  };
};
